import DOMPurify from 'dompurify'

/*
  The view's own pass over HTML the API has already sanitized ([SafeHtml]), so markup that got in
  some other way still never runs. DOMPurify removes scripts, event handlers and javascript: URLs.
*/

// Form elements, as the API's SafeHtml removes them; a label could otherwise work a control on the page.
const formTags = ['button', 'datalist', 'fieldset', 'form', 'input', 'keygen', 'label', 'legend', 'menuitem', 'optgroup', 'option', 'output', 'select', 'textarea']

// Never shown on a page, even where the API's email list allows them.
const forbiddenInFragments = ['style', ...formTags]

const xlinkNamespace = 'http://www.w3.org/1999/xlink'

/**
 * Opens links in a new tab, so a click never navigates the page or the frame. An SVG link's target
 * is its namespaced `xlink:href`, which a plain `[href]` selector misses.
 */
function newTabLinks(root: ParentNode): void {
  const links = Array.from(root.querySelectorAll('a, area')).filter(
    (link) => link.hasAttribute('href') || link.hasAttributeNS(xlinkNamespace, 'href'),
  )
  for (const link of links) {
    link.setAttribute('target', '_blank')
    // Its own rel, such as nofollow, is kept, less any opener.
    const rel = (link.getAttribute('rel') ?? '').toLowerCase().split(/\s+/u).filter((token) => token !== '' && token !== 'opener')
    link.setAttribute('rel', [...new Set([...rel, 'noopener', 'noreferrer'])].join(' '))
  }
}

const isSpace = (char: string) => char === ' ' || char === '\t' || char === '\n' || char === '\f' || char === '\r'

// The end of a tag's attribute, read as the HTML tokenizer reads it: a name, then an optional
// value, quoted or not. An unquoted value runs to a space or the tag's end, `/` included.
function attributeEnd(html: string, from: number): number {
  let at = from + 1
  while (at < html.length && !isSpace(html.charAt(at)) && !'/>='.includes(html.charAt(at))) at++
  let afterName = at
  while (afterName < html.length && isSpace(html.charAt(afterName))) afterName++
  if (html.charAt(afterName) !== '=') return at
  at = afterName + 1
  while (at < html.length && isSpace(html.charAt(at))) at++
  const quote = html.charAt(at)
  if (quote === '"' || quote === "'") {
    const close = html.indexOf(quote, at + 1)
    return close === -1 ? html.length : close + 1
  }
  while (at < html.length && !isSpace(html.charAt(at)) && html.charAt(at) !== '>') at++
  return at
}

// Where a comment that opened just before `from` ends, as the HTML tokenizer reads it: `<!-->` and
// `<!--->` end at once, and `--!>` closes one as `-->` does.
function commentEnd(html: string, from: number): number {
  if (html.startsWith('>', from)) return from + 1
  if (html.startsWith('->', from)) return from + 2
  const plain = html.indexOf('-->', from)
  const bang = html.indexOf('--!>', from)
  if (bang !== -1 && (plain === -1 || bang < plain)) return bang + 4
  return plain === -1 ? html.length : plain + 3
}

// Only an ASCII letter after `<` or `</` starts a tag.
const isLetter = (char: string) => /^[A-Za-z]$/u.test(char)

// Where `marker` ends, searching from `from`, or the end of the text.
function upTo(html: string, marker: string, from: number): number {
  const found = html.indexOf(marker, from)
  return found === -1 ? html.length : found + marker.length
}

// Elements whose content the tokenizer reads as text up to their end tag, outside SVG and MathML.
// `noscript` is left out: a parser without scripting, as DOMPurify's, reads its content as markup.
const textOnlyTags = new Set(['iframe', 'noembed', 'noframes', 'plaintext', 'script', 'style', 'textarea', 'title', 'xmp'])

// Start tags that end SVG or MathML content and are read as HTML, as are the end tags `</br>` and `</p>`.
const breakoutTags = new Set([
  'b', 'big', 'blockquote', 'body', 'br', 'center', 'code', 'dd', 'div', 'dl', 'dt', 'em', 'embed', 'h1', 'h2', 'h3', 'h4',
  'h5', 'h6', 'head', 'hr', 'i', 'img', 'li', 'listing', 'menu', 'meta', 'nobr', 'ol', 'p', 'pre', 'ruby', 's', 'small',
  'span', 'strong', 'strike', 'sub', 'sup', 'table', 'tt', 'u', 'ul', 'var',
])
const voidTags = new Set(['area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input', 'keygen', 'link', 'meta', 'param', 'source', 'track', 'wbr'])
const mathTextPoints = new Set(['mi', 'mn', 'mo', 'ms', 'mtext'])
const svgHtmlPoints = new Set(['desc', 'foreignobject', 'title'])

/** An element open inside SVG or MathML, as the tree builder keeps it. */
interface OpenElement {
  readonly name: string
  readonly space: 'html' | 'math' | 'svg'
  /** An HTML integration point, or a MathML text one: start tags inside it are read as HTML. */
  readonly htmlInside: boolean
}

const isForeign = (element: OpenElement | undefined): element is OpenElement => element !== undefined && element.space !== 'html'

// Whether the tree builder reads a start tag by HTML's rules with `top` as the current element.
function readsAsHtml(top: OpenElement | undefined, name: string): boolean {
  if (!isForeign(top)) return true
  if (top.space === 'math' && mathTextPoints.has(top.name)) return name !== 'mglyph' && name !== 'malignmark'
  return top.htmlInside || (top.space === 'math' && top.name === 'annotation-xml' && name === 'svg')
}

// Closes SVG and MathML back to the nearest HTML element or integration point.
function breakOut(elements: OpenElement[]): void {
  for (let top = elements.at(-1); isForeign(top) && !top.htmlInside; top = elements.at(-1)) elements.pop()
}

// Updates the open elements for a start tag; true when the tag is read as HTML.
function openTag(elements: OpenElement[], tag: Tag): boolean {
  const attributes = tag.kept.slice(1 + tag.name.length)
  const breaks = breakoutTags.has(tag.name) || (tag.name === 'font' && /[\s/](?:color|face|size)(?=[\s/=>]|$)/iu.test(attributes))
  if (breaks && !readsAsHtml(elements.at(-1), tag.name)) breakOut(elements)
  const top = elements.at(-1)
  if (readsAsHtml(top, tag.name)) {
    if (tag.name === 'svg' || tag.name === 'math') {
      if (!tag.selfClosing) elements.push({ name: tag.name, space: tag.name, htmlInside: false })
    } else if (top !== undefined && !voidTags.has(tag.name) && !textOnlyTags.has(tag.name)) {
      elements.push({ name: tag.name, space: 'html', htmlInside: false })
    }
    return true
  }
  if (!tag.selfClosing && isForeign(top)) {
    const htmlInside = top.space === 'svg'
      ? svgHtmlPoints.has(tag.name)
      : mathTextPoints.has(tag.name)
        || (tag.name === 'annotation-xml' && /[\s/]encoding\s*=\s*(["']?)(?:text\/html|application\/xhtml\+xml)\1(?=[\s/>]|$)/iu.test(attributes))
    elements.push({ name: tag.name, space: top.space, htmlInside })
  }
  return false
}

// Updates the open elements for an end tag: foreign content closes its own match, or else HTML's.
function closeTag(elements: OpenElement[], name: string): void {
  if (isForeign(elements.at(-1))) {
    if (name === 'br' || name === 'p') {
      breakOut(elements)
      return
    }
    const foreign = elements.findLastIndex((element, index) => element.name === name && elements.slice(index).every(isForeign))
    if (foreign !== -1) {
      elements.length = foreign
      return
    }
  }
  const html = elements.findLastIndex((element) => element.space === 'html' && element.name === name)
  if (html !== -1) elements.length = html
}

// Where the text of a text-only element ends: at its end tag, matched without regard to case.
function textEnd(html: string, from: number, name: string): number {
  if (name === 'plaintext') return html.length
  for (let at = html.indexOf('</', from); at !== -1; at = html.indexOf('</', at + 2)) {
    const after = html.charAt(at + 2 + name.length)
    if (html.slice(at + 2, at + 2 + name.length).toLowerCase() === name && (isSpace(after) || after === '/' || after === '>')) {
      return at
    }
  }
  return html.length
}

interface Tag {
  /** The tag as kept: a start tag without its handlers, an end tag as it is. */
  readonly kept: string
  readonly name: string
  readonly closed: boolean
  readonly selfClosing: boolean
  readonly end: number
}

// A start or end tag from `open`, its attributes read as the browser reads them.
function readTag(html: string, open: number, isEnd: boolean): Tag {
  const nameStart = open + (isEnd ? 2 : 1)
  let cursor = nameStart
  while (cursor < html.length && !isSpace(html.charAt(cursor)) && html.charAt(cursor) !== '/' && html.charAt(cursor) !== '>') cursor++
  const name = html.slice(nameStart, cursor).toLowerCase()
  let kept = html.slice(open, cursor)
  let selfClosing = false
  // Each attribute with what separates it from the last, kept unless its name starts with on.
  // An attribute right after a removed one, with nothing between, gets a space, so it stays one.
  let removed = false
  while (cursor < html.length && html.charAt(cursor) !== '>') {
    let start = cursor
    while (start < html.length && (isSpace(html.charAt(start)) || html.charAt(start) === '/')) start++
    if (start >= html.length || html.charAt(start) === '>') {
      selfClosing = start > cursor && html.charAt(start - 1) === '/'
      kept += html.slice(cursor, start)
      cursor = start
      break
    }
    const end = attributeEnd(html, start)
    if (!isEnd && html.slice(start, start + 2).toLowerCase() === 'on') {
      removed = true
    } else {
      kept += (removed && start === cursor ? ' ' : '') + html.slice(cursor, end)
      removed = false
    }
    cursor = end
  }
  const closed = cursor < html.length
  return { kept: closed ? `${kept}>` : kept, name, closed, selfClosing, end: closed ? cursor + 1 : html.length }
}

/**
 * Removes inline event handlers from the text before anything parses it. Chromium reports a handler
 * to the CSP as soon as HTML holding one is parsed, even inertly, as DOMPurify does. The text is
 * read as the browser's tokenizer reads it: handlers go from start tags only, so `<img/onerror=…>`
 * and a handler with no value go too, while comments and the text of elements such as `style` and
 * `title` stay as they are. DOMPurify still removes any handler this misses.
 */
export function stripInlineHandlers(html: string): string {
  let out = ''
  let at = 0
  // The elements open inside SVG or MathML, where no element's content is read as text.
  const elements: OpenElement[] = []
  while (at < html.length) {
    const open = html.indexOf('<', at)
    if (open === -1) break
    out += html.slice(at, open)
    const next = html.charAt(open + 1)
    if (isLetter(next) || (next === '/' && isLetter(html.charAt(open + 2)))) {
      const isEnd = next === '/'
      const tag = readTag(html, open, isEnd)
      out += tag.kept
      at = tag.end
      if (isEnd) {
        closeTag(elements, tag.name)
      } else if (openTag(elements, tag) && tag.closed && textOnlyTags.has(tag.name)) {
        const close = textEnd(html, at, tag.name)
        out += html.slice(at, close)
        at = close
      }
      continue
    }
    let end: number
    if (html.startsWith('<!--', open)) {
      end = commentEnd(html, open + 4)
    } else if (isForeign(elements.at(-1)) && html.startsWith('<![CDATA[', open)) {
      end = upTo(html, ']]>', open + 9)
    } else if (next === '!' || next === '?' || next === '/') {
      // `<!x>`, `<?x>` and `</1>` are comments up to the first `>`.
      end = upTo(html, '>', open + 2)
    } else {
      end = open + 1
    }
    out += html.slice(open, end)
    at = end
  }
  return out + html.slice(at)
}

/**
 * A fragment, such as a description, safe to put in the page: what DOMPurify allows, without
 * styles but `text-align`, as the API's default list, so nothing can cover the page around it.
 * Classes, ids and data attributes go too: the page's own CSS and scripts select on them, so a
 * fragment could borrow an overlay class or take another element's id.
 */
export function sanitizeFragment(html: string): string {
  const fragment = DOMPurify.sanitize(stripInlineHandlers(html), {
    FORBID_TAGS: forbiddenInFragments,
    FORBID_ATTR: ['class', 'id'],
    ALLOW_DATA_ATTR: false,
    RETURN_DOM_FRAGMENT: true,
  })
  for (const element of Array.from(fragment.querySelectorAll<HTMLElement>('[style]'))) {
    const align = element.style.textAlign
    element.removeAttribute('style')
    if (align !== '') {
      element.style.textAlign = align
    }
  }
  newTabLinks(fragment)
  const holder = document.createElement('div')
  holder.append(fragment)
  return holder.innerHTML
}

// A script scheme or an expression, which the API's email list removes too. DOMPurify leaves CSS alone.
const scriptInCss = /(?:javascript|vbscript)\s*:|expression\s*\(/iu

// CSS escapes: a hex code point with one optional space after it, or any other escaped character.
const cssEscape = /\\(?:([0-9a-f]{1,6})[ \t\n\r\f]?|(.))/giu

/**
 * A piece of CSS as the browser reads it: line continuations gone, escapes decoded, then the tabs
 * and line breaks a URL parser ignores taken out, so a backslash-newline or `\A` inside
 * `javascript:` still reads as one.
 */
function cssAsRead(css: string): string {
  return css
    .replace(/\\(?:\r\n|[\n\r\f])/gu, '')
    .replace(cssEscape, (_, hex: string | undefined, character: string | undefined) =>
      hex === undefined ? (character ?? '') : String.fromCodePoint(Math.min(Number.parseInt(hex, 16), 0x10ffff) || 0xfffd),
    )
    .replace(/[\t\n\r]/gu, '')
}

/**
 * Drops every declaration that names a script scheme or an expression once its comments are gone
 * and its escapes decoded, so `jav\61script:` and `java/**\/script:` are caught as plainly written.
 */
function withoutScriptsInCss(css: string): string {
  return css
    .replace(/\/\*[\s\S]*?\*\//gu, '')
    .replace(/[^;{}]+/gu, (declaration) => (scriptInCss.test(cssAsRead(declaration)) ? '' : declaration))
}

/** Takes script URLs out of a document's style attributes and style elements. */
function cleanStyles(root: Document): void {
  for (const element of Array.from(root.querySelectorAll('[style]'))) {
    element.setAttribute('style', withoutScriptsInCss(element.getAttribute('style') ?? ''))
  }
  for (const style of Array.from(root.querySelectorAll('style'))) {
    style.textContent = withoutScriptsInCss(style.textContent ?? '')
  }
}

// White paper and black ink, as email clients show a message, whatever the page's scheme; the
// document's own styles come after and win.
const paper = '<style>:root{color-scheme:light;background:Canvas;color:CanvasText}</style>'

/** What a document is called, and its language, for one that does not say. */
export interface DocumentFallbacks {
  readonly title: string
  /** A BCP 47 tag, such as `en`. */
  readonly lang: string
}

/**
 * A whole document, such as a sent email, for a sandboxed frame: its styles and images kept,
 * everything that could run removed, and links opening in a new tab. A document with no title or
 * language takes the fallbacks, so the frame's document is named and read in its language.
 */
export function sanitizeDocument(html: string, fallbacks?: DocumentFallbacks): string {
  const clean = DOMPurify.sanitize(stripInlineHandlers(html), {
    WHOLE_DOCUMENT: true,
    ADD_TAGS: ['style'],
    FORBID_TAGS: formTags,
  })
  const parsed = new DOMParser().parseFromString(clean, 'text/html')
  newTabLinks(parsed)
  cleanStyles(parsed)
  parsed.head.insertAdjacentHTML('afterbegin', paper)
  if (fallbacks !== undefined) {
    if (parsed.title.trim() === '') {
      parsed.title = fallbacks.title
    }
    if (parsed.documentElement.lang.trim() === '') {
      parsed.documentElement.lang = fallbacks.lang
    }
  }
  return `<!doctype html>${parsed.documentElement.outerHTML}`
}
